import { useQuery } from '@tanstack/react-query';
import { Alert, Stack, t, Text } from '@kanbios/ui';
import { api, unwrap } from '../../lib/api';
import { relativeTime } from '../../lib/format';

/**
 * D78, the server's Domaines tab: the addresses it serves that no longer point
 * to it (DNS read once an hour). None of them pointing here any more: the
 * server is probably obsolete, to put in reserve (D77) or decommission.
 */
export function ServerDnsNotice({ server }: { server: string }) {
  const list = useQuery({
    queryKey: ['addresses', 'dns', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/addresses', { params: { query: { server, pageSize: 100 } } })),
  });
  const data = list.data;
  if (!data) return null;
  const gone = data.items.filter(a => a.dns && (a.dns.status === 'elsewhere' || a.dns.status === 'none'));
  if (!gone.length) return null;
  const all = data.dns.here === 0 && data.dns.elsewhere + data.dns.none === data.dns.served;
  const checkedAt = gone.map(a => a.dns!.checkedAt).sort().at(-1)!;
  return (
    <Alert tone={all ? 'warn' : 'info'} title={t(all ? 'addresses.dns.noneHere' : 'addresses.dns.someGone', { n: gone.length, total: data.dns.served })}>
      <Stack gap={8}>
        <Text variant="body-sm">{t(all ? 'addresses.dns.noneHereBody' : 'addresses.dns.someGoneBody')}</Text>
        <Stack gap={4}>
          {gone.slice(0, 8).map(a => (
            <Text key={a.host} variant="body-sm">
              <Text variant="code">{a.host}</Text>{' · '}
              {a.dns!.status === 'none' ? t('addresses.dns.noRecord') : t('addresses.dns.pointsTo', { addresses: a.dns!.addresses.join(', ') })}
            </Text>
          ))}
        </Stack>
        {gone.length > 8 && <Text variant="body-sm" tone="mute">{t('addresses.dns.more', { n: gone.length - 8 })}</Text>}
        <Text variant="caption" tone="mute">{t('addresses.dns.checked', { when: relativeTime(checkedAt) })}</Text>
      </Stack>
    </Alert>
  );
}
